<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Настройка меню CRM</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="chto-za-menyu"
            class="typography__headline2"
          >
            Что за&nbsp;меню
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              Навигация по&nbsp;CRM MyDonut происходит в&nbsp;удобном меню слева на&nbsp;десктопной версии или при
              переходе в&nbsp;меню из&nbsp;шапки сайта в&nbsp;мобильной версии.
            </p>

            <p>Из&nbsp;него можно попасть в&nbsp;любой нужный раздел.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/nastroyka-menyu-crm-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="nastroyka"
            class="typography__headline2"
          >
            Настройка
          </FaqArticlesBlockHeading>

          <p>Перейти к&nbsp;настройке меню можно нажав кнопку &laquo;Настроить панель&raquo; внизу неё.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/nastroyka-menyu-crm-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>После перехода в&nbsp;режим настройки вы&nbsp;увидите переключатели у&nbsp;соответствующих разделов.</p>

            <p>
              Переключатели влияют на&nbsp;видимость разделов в&nbsp;этом меню, доступность их&nbsp;настроек
              в&nbsp;<BaseLink
                target="_blank"
                to="/faq/articles/konstruktor-sayta"
                >конструкторе сайта</BaseLink
              >
              и&nbsp;видимость функционала клиентом на&nbsp;вашем сайте . Для вашего удобства, если
              вы&nbsp;не&nbsp;пользуетесь каким-то функционалом, можно скрыть ненужные.
            </p>

            <p>
              Переключатель у&nbsp;раздела &laquo;Услуг&raquo; скроет из&nbsp;меню разделы &laquo;Услуги&raquo;
              и&nbsp;&laquo;Заявки&raquo;. Будет удобно, если вы&nbsp;не&nbsp;пользуетесь услугами и&nbsp;заявками
              на&nbsp;них.
            </p>

            <p>
              Переключатель у&nbsp;раздела &laquo;Журнал записей&raquo; скроет из&nbsp;меню разделы &laquo;Журнал
              записей&raquo; и&nbsp;&laquo;Расписание сотрудника&raquo;. Будет удобно, если вы&nbsp;используете услуги
              только для заявок, без онлайн-записей, или вообще не&nbsp;пользуетесь услугами.
            </p>

            <p>
              Переключатель у&nbsp;раздела &laquo;Товары&raquo; скроет из&nbsp;меню разделы &laquo;Товары&raquo;
              и&nbsp;&laquo;Заказы&raquo;. Будет удобно, если вы&nbsp;не&nbsp;продаете товары.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/nastroyka-menyu-crm-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>После настройки нужных разделов нажмите кнопку &laquo;Сохранить&raquo; внизу меню.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/nastroyka-menyu-crm-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>По&nbsp;завершению сохранения изменений вы&nbsp;увидите обновленное меню со&nbsp;скрытыми разделами.</p>

            <p>Настроить панель заново и&nbsp;вернуть или отключить нужные разделы можно тем&nbsp;же самым способом.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/nastroyka-menyu-crm-5.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Данные проекта",
    url: "/faq/categories/dannyye-proyekta",
  },
  {
    name: "Настройка меню CRM",
  },
];

const menu = [
  {
    name: "Что за&nbsp;меню",
    url: "chto-za-menyu",
  },
  {
    name: "Настройка",
    url: "nastroyka",
  },
];

const relatedArticles = [
  {
    name: "Общие настройки",
    slug: "obshchiye-nastroyki",
  },
  {
    name: "Контакты и&nbsp;данные филиалов",
    slug: "kontakty-i-dannye-filialov",
  },
  {
    name: "Язык и&nbsp;регион",
    slug: "yazyk-i-region",
  },
  {
    name: "Уведомления",
    slug: "uvedomleniya",
  },
];

useSeoMeta({
  title: "Настройка меню CRM",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

p a {
  display: contents;
}
</style>
